// src/internal/components/totp-input/index.tsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import isEqual from "just-compare";
import { Stack } from "@mui/material";
import { useAuthContext } from "@clara/auth";
import { useInterval, usePrevious } from "@clara/hooks";
import { useT } from "@clara/locale";
import { Banner, PinInput } from "@clara/ui";
import { jsx, jsxs } from "react/jsx-runtime";
var defaultDuration = 6e5;
var TOTPInput = (props) => {
  const { t } = useT("auth");
  const {
    totp: {
      getTOTPInputProps,
      codes: {
        value: { token }
      },
      setIsTimeup,
      isCodeExpired,
      isCodeInvalid,
      setTotpError
    }
  } = useAuthContext();
  const [delay, setDelay] = useState(null);
  const [duration, setDuration] = useState(
    props.duration || defaultDuration
  );
  const prevToken = usePrevious(token);
  let intervalRef = useRef({
    minutes: Math.floor(duration / (1e3 * 60) % 60),
    seconds: Math.floor(duration / 1e3 % 60)
  });
  const { minutes, seconds } = intervalRef.current;
  const startTimer = useCallback((delay2) => {
    setDelay(delay2);
  }, []);
  const isTimeUp = seconds === 0 && minutes === 0;
  useInterval(
    () => {
      let minutes2 = Math.floor(duration / (1e3 * 60) % 60);
      let seconds2 = Math.floor(duration / 1e3 % 60);
      intervalRef.current = {
        minutes: minutes2,
        seconds: seconds2
      };
      setDuration(duration - delay);
    },
    !!delay && duration > -1 ? delay : null
  );
  useEffect(() => {
    if (isTimeUp) {
      setDelay(null);
      setDuration(props.duration || defaultDuration);
    }
  }, [isTimeUp, props.duration]);
  const isInvalid = useMemo(() => {
    return isTimeUp || isCodeInvalid;
  }, [isTimeUp, isCodeInvalid]);
  const resetTimer = useCallback(() => {
    setDelay(null);
    setDuration(props.duration || defaultDuration);
    setIsTimeup(false);
    startTimer(1e3);
  }, [startTimer]);
  useEffect(() => {
    startTimer(1e3);
    return () => {
      setTotpError(null);
    };
  }, []);
  useEffect(() => {
    if (!!token && !isEqual(token, prevToken)) {
      resetTimer();
      setTotpError(null);
    }
  }, [token, prevToken, resetTimer, setTotpError]);
  const helperText = useMemo(() => {
    let text = `${minutes < 10 ? "0" : ""}${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
    if (isTimeUp && !isCodeExpired) {
      text = t("totp.input.error.timer");
    }
    if (isCodeInvalid) {
      text = t("totp.invalid.code");
    }
    return text;
  }, [minutes, seconds, isTimeUp, isCodeExpired, isCodeInvalid, t]);
  useEffect(() => {
    setIsTimeup(isTimeUp);
  }, [isTimeUp]);
  useEffect(() => {
    if (isCodeExpired) {
      setDuration(0);
    }
  }, [isCodeExpired]);
  return /* @__PURE__ */ jsxs(Stack, { spacing: 2, children: [
    /* @__PURE__ */ jsx(
      PinInput,
      {
        ...getTOTPInputProps({
          onComplete: (codes) => {
          },
          onChange: (codes) => {
          },
          label: t("totp.input.label"),
          helperText,
          isInvalid,
          ...props
        })
      }
    ),
    isCodeExpired && /* @__PURE__ */ jsx(Banner, { status: "critical", children: t("totp.expired.code") })
  ] });
};

export {
  TOTPInput
};
